package addedittravelagent

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.file.domain.model.FileModel
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.file.ui.FileState
import com.essntl.features.file.ui.FileViewModel
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.features.travelagent.domain.model.TravelAgentModel
import com.essntl.features.travelagent.domain.repository.TravelAgentRepository
import com.essntl.toastmanager.ToastManager
import com.mohamedrejeb.calf.core.PlatformContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AddEditTravelAgentViewModel(
    private val travelAgentRepository: TravelAgentRepository,
    private val proposalRepository: ProposalRepository,
    private val itineraryRepository: ItineraryRepository,
    fileRepository: FileRepository,
    private val toastManager: ToastManager,
    private val state: MutableStateFlow<AddEditTravelAgentState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    private val fileViewModel = FileViewModel(
        fileRepository = fileRepository,
        scope = scope,
        loadingManager = loadingManager,
    )

    init {
        collectFileState()
    }

    private var getLogoJob: Job? = null
    private fun collectFileState() {
        // collecting logos
        getLogoJob?.cancel()
        getLogoJob = scope.launch {
            fileViewModel.state.collect { data ->
                isTravelAgentChanged(fileState = data)
            }
        }
    }

    private fun isTravelAgentChanged(fileState: FileState? = null) {
        // Compare logos
        val currentFileState = fileState ?: state.value.fileState
        val oldLogo = currentFileState.getAllFile.data?.firstOrNull()?.id

        val oldTravelAgent = state.value.getTravelAgent.data ?: TravelAgentModel.empty
        val newTravelAgent = state.value.travelAgent.value
        val changedFields = oldTravelAgent.getChangedFields(newTravelAgent)

        state.update {
            it.copy(
                fileState = currentFileState,
                isEditEnabled = !changedFields.isEmpty
            )
        }
    }

    fun onEvent(event: AddEditTravelAgentEvent) {
        when (event) {
            is AddEditTravelAgentEvent.GetTravelAgent -> getTravelAgent(event)
            is AddEditTravelAgentEvent.UpdateTravelAgentFields -> updateTravelAgentFields(event)
            is AddEditTravelAgentEvent.AddTravelAgent -> addTravelAgent(event)
            is AddEditTravelAgentEvent.EditTravelAgent -> editTravelAgent(event)
            is AddEditTravelAgentEvent.ClearTravelAgentState -> clearTravelAgentState()
            is AddEditTravelAgentEvent.ClearSuccessState -> clearSuccessState()
            is AddEditTravelAgentEvent.AddLogo -> addLogo(event)
            is AddEditTravelAgentEvent.DeleteLogo -> deleteLogo()
        }
    }

    private fun addLogo(event: AddEditTravelAgentEvent.AddLogo) {

    }

    private fun deleteLogo() {

    }

    private var getTravelAgentJob: Job? = null
    private fun getTravelAgent(event: AddEditTravelAgentEvent.GetTravelAgent) {
        if (state.value.getTravelAgent.data?.id == event.travelAgentId) return

        fileViewModel.setState(State())

        state.value.travelAgent.value = TravelAgentModel.empty.copy(id = event.travelAgentId)

        // Make sure to cancel the previous job
        // The user can switch between ta quickly before the job is finished,
        // In that case we don't want to show the result of the previous job
        getTravelAgentJob?.cancel()
        getTravelAgentJob = getData(
            getData = { travelAgentRepository.getById(event.travelAgentId) },
            getState = { state.value.getTravelAgent },
            setState = {
                state.value = state.value.copy(
                    getTravelAgent = it,
                    isEditEnabled = false,
                )
            },
            onSuccess = { ta ->

                state.value.travelAgent.value = ta

                getProposals(ta.id)
            },
        )
    }

    private fun updateTravelAgentFields(event: AddEditTravelAgentEvent.UpdateTravelAgentFields) {
        state.value.travelAgent.value = event.travelAgent
        isTravelAgentChanged()
    }

    private fun addTravelAgent(event: AddEditTravelAgentEvent.AddTravelAgent) {
        scope.launch {
            state.update {
                it.copy(
                    addEditTravelAgent = State.loading(),
                )
            }

            if (!insertFiles(event.context)) return@launch

            getData(
                getData = { travelAgentRepository.insert(state.value.travelAgent.value) },
                getState = { state.value.addEditTravelAgent },
                setState = { state.value = state.value.copy(addEditTravelAgent = it) },
                onSuccess = { ta ->
                    state.update {
                        it.copy(
                            getTravelAgent = State.success(ta),
                        )
                    }
                    state.value.travelAgent.value = ta

                    toastManager.success(
                        description = "Travel agent created successfully",
                    )
                },
                ignoreLoading = true,
            )
        }
    }

    private fun editTravelAgent(event: AddEditTravelAgentEvent.EditTravelAgent) {
        scope.launch {
            state.update {
                it.copy(
                    addEditTravelAgent = State.loading(),
                )
            }


            // If insert images fails, we don't want to continue
            if (!insertFiles(event.context)) return@launch

            getData(
                getData = { travelAgentRepository.update(state.value.travelAgent.value) },
                getState = { state.value.addEditTravelAgent },
                setState = { state.value = state.value.copy(addEditTravelAgent = it) },
                onSuccess = { ta ->
                    state.update {
                        it.copy(
                            getTravelAgent = State.success(ta),
                        )
                    }
                    state.value.travelAgent.value = ta

                    toastManager.success(
                        description = "Travel agent updated successfully",
                    )
                },
                ignoreLoading = true,
            )
        }
    }

    private fun clearSuccessState() {
        state.value = state.value.copy(
            addEditTravelAgent = State(),
            isEditEnabled = false,
        )
    }

    private fun clearTravelAgentState() {
        state.value = state.value.copy(
            getTravelAgent = State(),
            addEditTravelAgent = State(),
            isEditEnabled = false,
        )
        state.value.travelAgent.value =
            TravelAgentModel.empty.copy(commissionType = TravelAgentModel.CommissionType.Net)

        fileViewModel.setState(State())
    }

    private fun getProposals(travelAgentId: String) =
        getData(
            getData = { proposalRepository.getProposalsByTravelAgentId(travelAgentId = travelAgentId) },
            getState = { state.value.getProposals },
            setState = { state.value = state.value.copy(getProposals = it) },
            onSuccess = { proposals ->
                if (proposals.isNotEmpty())
                    getItineraries(proposals.map { proposal -> proposal.id })
                else
                    state.update {
                        it.copy(
                            getItineraries = State.success(emptyList()),
                        )
                    }
            },
        )

    private fun getItineraries(proposalIds: List<String>) =
        getData(
            getData = { itineraryRepository.getItinerariesByProposalIds(proposalIds) },
            getState = { state.value.getItineraries },
            setState = { state.value = state.value.copy(getItineraries = it) },
        )

    private suspend fun insertFiles(context: PlatformContext): Boolean {
        val agencyName = state.value.travelAgent.value.contact?.organization?.name

        val keywords = if (!agencyName.isNullOrEmpty())
            listOf(agencyName) else
            emptyList()

        return fileViewModel.insertFiles(
            folderName = "ta_logos",
            bucketName = "images",
            entityType = "travel_agent",
            keywords = keywords,
            context = context,
            onSuccess = {
            },
            onFailure = {
                state.update {
                    it.copy(
                        addEditTravelAgent = State.failure(),
                    )
                }
            },
        )
    }
}